import {
  useQuery,
  useMutation,
  UseQueryOptions,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import apiClient from '../client';
import {
  UserPhotoView,
  UserPhotoUploadInput,
  UserPhotoSetDefaultInput,
  UserProfileView,
  MeetingAnnouncementPhotoView,
  MeetingAnnouncementPhotoFromPredefinedInput,
  MeetingAnnouncementPhotoSetDefaultInput,
  PredefinedAnnouncementImageView,
  ApiError,
  ApiPlatformCollectionResponse,
  extractCollectionItems,
} from '../types';
import { profileKeys } from './useProfile';

export const photoKeys = {
  userPhotos: ['userPhotos'] as const,
  announcementPhotos: (announcementId: string) =>
    ['announcementPhotos', announcementId] as const,
  predefinedImages: ['predefinedImages'] as const,
};

export const useUserPhotos = (
  options?: Omit<
    UseQueryOptions<UserPhotoView[], ApiError>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<UserPhotoView[], ApiError>({
    queryKey: photoKeys.userPhotos,
    queryFn: async () => {
      const response = await apiClient.get<
        ApiPlatformCollectionResponse<UserPhotoView>
      >('/users/me/photos');
      return extractCollectionItems(response.data);
    },
    ...options,
  });
};

export const useUploadUserPhoto = (
  options?: UseMutationOptions<UserPhotoView, ApiError, UserPhotoUploadInput>,
) => {
  const queryClient = useQueryClient();

  return useMutation<UserPhotoView, ApiError, UserPhotoUploadInput>({
    mutationFn: async (data: UserPhotoUploadInput) => {
      const response = await apiClient.post<UserPhotoView>(
        '/users/me/photos',
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: photoKeys.userPhotos });
      queryClient.invalidateQueries({ queryKey: profileKeys.me() });
    },
    ...options,
  });
};

export const useSetUserPhotoDefault = (
  options?: UseMutationOptions<
    UserPhotoView,
    ApiError,
    { photoId: number; data: UserPhotoSetDefaultInput }
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<
    UserPhotoView,
    ApiError,
    { photoId: number; data: UserPhotoSetDefaultInput }
  >({
    mutationFn: async ({
      photoId,
      data,
    }: {
      photoId: number;
      data: UserPhotoSetDefaultInput;
    }) => {
      const response = await apiClient.patch<UserPhotoView>(
        `/users/me/photos/${photoId}`,
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: photoKeys.userPhotos });
      queryClient.invalidateQueries({ queryKey: profileKeys.me() });
    },
    ...options,
  });
};

export const useDeleteUserPhoto = (
  options?: UseMutationOptions<void, ApiError, number>,
) => {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, number>({
    mutationFn: async (photoId: number) => {
      await apiClient.delete(`/users/me/photos/${photoId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: photoKeys.userPhotos });
      queryClient.invalidateQueries({ queryKey: profileKeys.me() });
    },
    ...options,
  });
};

export const useAnnouncementPhotos = (
  announcementId: string,
  options?: Omit<
    UseQueryOptions<MeetingAnnouncementPhotoView[], ApiError>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<MeetingAnnouncementPhotoView[], ApiError>({
    queryKey: photoKeys.announcementPhotos(announcementId),
    queryFn: async () => {
      const response = await apiClient.get<
        ApiPlatformCollectionResponse<MeetingAnnouncementPhotoView>
      >(`/meeting-announcements/${announcementId}/photos`);
      return extractCollectionItems(response.data);
    },
    enabled: !!announcementId,
    ...options,
  });
};

export const useUploadAnnouncementPhoto = (
  options?: UseMutationOptions<
    MeetingAnnouncementPhotoView,
    ApiError,
    {
      announcementId: string;
      file: File;
      isDefault?: boolean;
    }
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<
    MeetingAnnouncementPhotoView,
    ApiError,
    {
      announcementId: string;
      file: File;
      isDefault?: boolean;
    }
  >({
    mutationFn: async ({
      announcementId,
      file,
      isDefault,
    }: {
      announcementId: string;
      file: File;
      isDefault?: boolean;
    }) => {
      const formData = new FormData();
      formData.append('file', file);
      if (isDefault !== undefined) {
        formData.append('isDefault', String(isDefault));
      }

      const response =
        await apiClient.postFormData<MeetingAnnouncementPhotoView>(
          `/meeting-announcements/${announcementId}/photos/upload`,
          formData,
        );
      return response.data;
    },
    onSuccess: (
      data: MeetingAnnouncementPhotoView,
      variables: { announcementId: string; file: File; isDefault?: boolean },
    ) => {
      queryClient.invalidateQueries({
        queryKey: photoKeys.announcementPhotos(variables.announcementId),
      });
    },
    ...options,
  });
};

export const useAddAnnouncementPhotoFromPredefined = (
  options?: UseMutationOptions<
    MeetingAnnouncementPhotoView,
    ApiError,
    {
      announcementId: string;
      data: MeetingAnnouncementPhotoFromPredefinedInput;
    }
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<
    MeetingAnnouncementPhotoView,
    ApiError,
    {
      announcementId: string;
      data: MeetingAnnouncementPhotoFromPredefinedInput;
    }
  >({
    mutationFn: async ({
      announcementId,
      data,
    }: {
      announcementId: string;
      data: MeetingAnnouncementPhotoFromPredefinedInput;
    }) => {
      const response = await apiClient.post<MeetingAnnouncementPhotoView>(
        `/meeting-announcements/${announcementId}/photos/from-predefined`,
        data,
      );
      return response.data;
    },
    onSuccess: (
      data: MeetingAnnouncementPhotoView,
      variables: {
        announcementId: string;
        data: MeetingAnnouncementPhotoFromPredefinedInput;
      },
    ) => {
      queryClient.invalidateQueries({
        queryKey: photoKeys.announcementPhotos(variables.announcementId),
      });
    },
    ...options,
  });
};

export const useSetAnnouncementPhotoDefault = (
  options?: UseMutationOptions<
    MeetingAnnouncementPhotoView,
    ApiError,
    {
      announcementId: string;
      photoId: number;
      data: MeetingAnnouncementPhotoSetDefaultInput;
    }
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<
    MeetingAnnouncementPhotoView,
    ApiError,
    {
      announcementId: string;
      photoId: number;
      data: MeetingAnnouncementPhotoSetDefaultInput;
    }
  >({
    mutationFn: async ({
      announcementId,
      photoId,
      data,
    }: {
      announcementId: string;
      photoId: number;
      data: MeetingAnnouncementPhotoSetDefaultInput;
    }) => {
      const response = await apiClient.patch<MeetingAnnouncementPhotoView>(
        `/meeting-announcements/${announcementId}/photos/${photoId}`,
        data,
      );
      return response.data;
    },
    onSuccess: (
      data: MeetingAnnouncementPhotoView,
      variables: {
        announcementId: string;
        photoId: number;
        data: MeetingAnnouncementPhotoSetDefaultInput;
      },
    ) => {
      queryClient.invalidateQueries({
        queryKey: photoKeys.announcementPhotos(variables.announcementId),
      });
    },
    ...options,
  });
};

export const useDeleteAnnouncementPhoto = (
  options?: UseMutationOptions<
    void,
    ApiError,
    { announcementId: string; photoId: number }
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    ApiError,
    { announcementId: string; photoId: number }
  >({
    mutationFn: async ({
      announcementId,
      photoId,
    }: {
      announcementId: string;
      photoId: number;
    }) => {
      await apiClient.delete(
        `/meeting-announcements/${announcementId}/photos/${photoId}`,
      );
    },
    onSuccess: (
      data: void,
      variables: { announcementId: string; photoId: number },
    ) => {
      queryClient.invalidateQueries({
        queryKey: photoKeys.announcementPhotos(variables.announcementId),
      });
    },
    ...options,
  });
};

export const usePredefinedAnnouncementImages = (
  options?: Omit<
    UseQueryOptions<PredefinedAnnouncementImageView[], ApiError>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<PredefinedAnnouncementImageView[], ApiError>({
    queryKey: photoKeys.predefinedImages,
    queryFn: async () => {
      const response = await apiClient.get<
        ApiPlatformCollectionResponse<PredefinedAnnouncementImageView>
      >('/meeting-announcements/predefined-images');
      return extractCollectionItems(response.data);
    },
    ...options,
  });
};

export const useLockUserPhoto = (
  options?: UseMutationOptions<UserPhotoView, ApiError, { photoId: number }>,
) => {
  const queryClient = useQueryClient();

  return useMutation<UserPhotoView, ApiError, { photoId: number }>({
    mutationFn: async ({ photoId }: { photoId: number }) => {
      const response = await apiClient.patch<UserPhotoView>(
        `/users/me/photos/${photoId}/lock`,
      );
      return response.data;
    },
    onMutate: async ({ photoId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: profileKeys.me() });

      // Snapshot the previous value
      const previousProfile = queryClient.getQueryData<UserProfileView>(
        profileKeys.me(),
      );

      // Optimistically update to the new value
      if (previousProfile) {
        queryClient.setQueryData<UserProfileView>(profileKeys.me(), {
          ...previousProfile,
          photos: previousProfile.photos.map(photo =>
            photo.id === photoId ? { ...photo, isLocked: true } : photo,
          ),
        });
      }

      return { previousProfile };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousProfile) {
        queryClient.setQueryData(profileKeys.me(), context.previousProfile);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: photoKeys.userPhotos });
      queryClient.invalidateQueries({ queryKey: profileKeys.me() });
    },
    ...options,
  });
};

export const useUnlockUserPhoto = (
  options?: UseMutationOptions<UserPhotoView, ApiError, { photoId: number }>,
) => {
  const queryClient = useQueryClient();

  return useMutation<UserPhotoView, ApiError, { photoId: number }>({
    mutationFn: async ({ photoId }: { photoId: number }) => {
      const response = await apiClient.patch<UserPhotoView>(
        `/users/me/photos/${photoId}/unlock`,
      );
      return response.data;
    },
    onMutate: async ({ photoId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: profileKeys.me() });

      // Snapshot the previous value
      const previousProfile = queryClient.getQueryData<UserProfileView>(
        profileKeys.me(),
      );

      // Optimistically update to the new value
      if (previousProfile) {
        queryClient.setQueryData<UserProfileView>(profileKeys.me(), {
          ...previousProfile,
          photos: previousProfile.photos.map(photo =>
            photo.id === photoId ? { ...photo, isLocked: false } : photo,
          ),
        });
      }

      return { previousProfile };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousProfile) {
        queryClient.setQueryData(profileKeys.me(), context.previousProfile);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: photoKeys.userPhotos });
      queryClient.invalidateQueries({ queryKey: profileKeys.me() });
    },
    ...options,
  });
};
